import { Link } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import "../styles/animations.css";

type Post = {
  id: string;
  title: string;
  date: string;
  body: ReactNode;
};

const posts: Post[] = [
  {
    id: "intro",
    title: "Raspberry Pi Chatbot Series",
    date: "2024-02-24",
    body: (
      <>
        <p>
          I had a Raspberry Pi 4 lying around for years, untouched. Once I had
          room to explore it, I wanted a simple goal: speak to it, and have it
          speak back with an AI response.
        </p>
        <p className="mt-4">
          Why the Pi 4? Because I don’t have a 5. It still has a quad-core ARM
          Cortex-A72 and 4GB of RAM — enough to make an offline assistant an
          interesting challenge.
        </p>
        <h3 className="text-white font-semibold mt-6 mb-2">Three parts</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-300">
          <li>
            <strong className="text-gray-100">Text to speech (TTS)</strong> —
            compare espeak, pico, festival, and a cloud TTS option.
          </li>
          <li>
            <strong className="text-gray-100">Speech to text (STT)</strong> —
            mic setup, pocketsphinx vs Vosk, accuracy vs latency.
          </li>
          <li>
            <strong className="text-gray-100">LLMs on 4GB</strong> — running
            LLaMA-class models on a machine that barely has room for them.
          </li>
        </ol>
        <p className="mt-4 text-sm text-gray-400">
          Specs: Broadcom BCM2711, 4GB LPDDR4, dual-band Wi‑Fi, Bluetooth 5.0,
          Gigabit Ethernet, microSD storage.
        </p>
      </>
    ),
  },
  {
    id: "tts",
    title: "#1: Text-to-speech (TTS)",
    date: "2024-02-24",
    body: (
      <>
        <p>
          TTS is the easiest leg of the stack — no mic config, just get words
          out of the speaker. I imaged Raspberry Pi OS, then compared offline
          engines before trying a cloud API.
        </p>
        <ul className="mt-4 space-y-3 text-gray-300">
          <li>
            <strong className="text-gray-100">espeak</strong> — trivial to
            install; robotic, tinny, and oddly British. Still the most
            “human-like” of the offline options I tried.
          </li>
          <li>
            <strong className="text-gray-100">pico</strong> — muffled, Charlie
            Brown teacher energy; better raw audio quality than espeak for me.
          </li>
          <li>
            <strong className="text-gray-100">festival</strong> — sounded like a
            robot locked in a tin can. Skip.
          </li>
          <li>
            <strong className="text-gray-100">Voice RSS</strong> — free tier for
            testing; not meaningfully better than offline for this use case. If
            you want cloud quality, Polly or Google Cloud are better bets.
          </li>
        </ul>
        <p className="mt-4">
          Verdict: pico for quality, espeak for character. Offline was good
          enough for the assistant loop.
        </p>
      </>
    ),
  },
  {
    id: "stt",
    title: "#2: Speech-to-text (STT)",
    date: "2024-02-25",
    body: (
      <>
        <p>
          STT is much harder — third-party mics, algorithms, and room noise all
          fight you. I stayed offline where I could. Mozilla DeepSpeech looked
          promising until upstream development stopped and packages became hard
          to install, so I moved on.
        </p>
        <p className="mt-4">
          <strong className="text-gray-100">pocketsphinx</strong> was easy to
          wire up with <code className="text-emerald-300/90">LiveSpeech()</code>
          , but live accuracy was rough — lots of repetition needed, and
          decode latency made the transcript wander. File-based recognition on
          a clean generated WAV was more consistent.
        </p>
        <p className="mt-4">
          <strong className="text-gray-100">Vosk</strong> with the small
          English RPi model was clearly more accurate, though slower (often
          10+ seconds) and occasionally needed a restart. ALSA / JACK noise in
          the logs was mostly suppressible; the recognition quality was worth
          the wait. Slow and right beats fast and wrong.
        </p>
      </>
    ),
  },
  {
    id: "llm",
    title: "#3: LLM — it spoke, then died",
    date: "2024-02-28",
    body: (
      <>
        <p>
          I saved the model for last. Cloud APIs would have been easy; I wanted
          to see how far 4GB of RAM could go offline. After poking around
          Alpaca-era projects (mostly inactive), I landed on LLaMA-class GGUF
          builds small enough to fit.
        </p>
        <p className="mt-4">
          A 2-bit ~2.8GB model actually answered questions over SSH — until it
          didn’t. Mid-reply on an existential “who built you?” the Pi
          hard-restarted and dropped the connection. Repeatable under memory
          pressure, not a ghost story. Smaller 3-bit quantizations from
          community Hugging Face uploads were the next experiments.
        </p>
        <p className="mt-4 text-gray-400 text-sm">
          Lesson: a Pi 4 can host a tiny conversational stack, but LLMs on 4GB
          are living on the edge. Fun edge, though.
        </p>
      </>
    ),
  },
];

function RaspberryPi() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <main
      className="flex-1 w-full px-4 md:px-6 py-12 max-w-3xl mx-auto"
      style={{ animation: isLoaded ? "fadeIn 0.6s ease-out" : undefined }}
    >
      <Link
        to="/"
        className="inline-flex items-center text-emerald-400 hover:text-emerald-300 mb-8 transition-colors font-semibold"
      >
        <span className="mr-2">←</span> Back home
      </Link>

      <header className="mb-10">
        <p className="text-xs uppercase tracking-[0.14em] text-emerald-400/90 font-semibold mb-2">
          Projects
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
          Raspberry Pi
        </h1>
        <p className="text-gray-300 mt-3 text-lg leading-relaxed">
          Building a voice AI assistant on a Pi 4 — TTS, STT, and a local LLM
          that occasionally reboots itself mid-sentence.
        </p>
        <figure className="mt-6 overflow-hidden rounded-xl border border-white/10">
          <img
            src="https://themetadreams.com/wp-content/uploads/2024/02/raspi-1024x768.jpg"
            alt="Raspberry Pi project setup"
            className="w-full h-auto object-cover"
          />
        </figure>
      </header>

      <div className="space-y-10">
        {posts.map((post) => (
          <article
            key={post.id}
            id={post.id}
            className="border-t border-white/10 pt-8"
          >
            <h2 className="text-2xl font-bold text-white">{post.title}</h2>
            <p className="font-mono text-xs text-gray-500 mt-1">{post.date}</p>
            <div className="mt-4 text-gray-300 leading-relaxed space-y-0">
              {post.body}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-14 text-center text-gray-500 text-sm border-t border-white/10 pt-8">
        <p>© {new Date().getFullYear()} Walt Yao. All rights reserved.</p>
      </div>
    </main>
  );
}

export default RaspberryPi;
